import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GripVertical, Eye, EyeOff, Search, Store, SlidersHorizontal, Package } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { useAuth } from "@/lib/auth";

const LAYOUT_TABS = [
  { key: "card", label: "Card Vetrina",
    hint: "Configura i campi mostrati nella card prodotto del catalogo brand.",
    endpoint: "/vetrina-layout" },
  { key: "detail", label: "Pagina Dettaglio",
    hint: "Configura i campi mostrati nella pagina di dettaglio prodotto del catalogo.",
    endpoint: "/vetrina-detail-layout" },
];

/* ------------------------------- Settings tab ------------------------------ */
function ImpostazioniTab() {
  const { canWrite } = useAuth();
  const [activeTab, setActiveTab] = useState("card");
  const [layouts, setLayouts] = useState({ card: [], detail: [] });
  const [baseline, setBaseline] = useState({ card: null, detail: null });
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    api.get("/vetrina-layout").then(r => {
      setLayouts(s => ({ ...s, card: r.data }));
      setBaseline(s => ({ ...s, card: JSON.stringify(r.data) }));
    });
    api.get("/vetrina-detail-layout").then(r => {
      setLayouts(s => ({ ...s, detail: r.data }));
      setBaseline(s => ({ ...s, detail: JSON.stringify(r.data) }));
    });
  }, []);

  const blocks = layouts[activeTab] || [];
  const setBlocks = (next) => setLayouts(s => ({ ...s, [activeTab]: next }));

  const move = (from, to) => {
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setBlocks(next);
  };
  const toggle = (i) => {
    const next = [...blocks];
    next[i] = { ...next[i], is_enabled: !next[i].is_enabled };
    setBlocks(next);
  };
  const save = async () => {
    const endpoint = LAYOUT_TABS.find(t => t.key === activeTab).endpoint;
    await api.put(endpoint, { blocks });
    setBaseline(s => ({ ...s, [activeTab]: JSON.stringify(blocks) }));
    toast.success(activeTab === "card" ? "Layout card salvato" : "Layout pagina dettaglio salvato");
    return true;
  };

  // Guard: save whichever sub-layout(s) still differ from the loaded baseline.
  const cardDirty = canWrite && baseline.card !== null && baseline.card !== JSON.stringify(layouts.card);
  const detailDirty = canWrite && baseline.detail !== null && baseline.detail !== JSON.stringify(layouts.detail);
  useUnsavedChanges(cardDirty || detailDirty, async () => {
    if (cardDirty) {
      await api.put("/vetrina-layout", { blocks: layouts.card });
      setBaseline(s => ({ ...s, card: JSON.stringify(layouts.card) }));
    }
    if (detailDirty) {
      await api.put("/vetrina-detail-layout", { blocks: layouts.detail });
      setBaseline(s => ({ ...s, detail: JSON.stringify(layouts.detail) }));
    }
    toast.success("Layout salvato");
    return true;
  });

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx !== null && dragIdx !== i) move(dragIdx, i);
    setDragIdx(null);
  };

  const meta = LAYOUT_TABS.find(t => t.key === activeTab);

  return (
    <div data-testid="catalog-settings-tab">
      <div className="flex justify-between items-end mb-5">
        <div className="text-sm text-[#707070]">{meta.hint}</div>
        {canWrite && <button onClick={save} className="btn-primary" data-testid="vetrina-layout-save-btn">Salva</button>}
      </div>

      <div className="flex gap-6 border-b border-[#E5E5E0] mb-6 max-w-2xl" data-testid="vetrina-tabs">
        {LAYOUT_TABS.map(t => (
          <button key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`pb-3 text-xs tracking-luxury uppercase transition-colors ${
                    activeTab === t.key
                      ? "text-[#1A1A1A] border-b-2 border-[#1A1A1A] -mb-px font-medium"
                      : "text-[#707070] hover:text-[#1A1A1A]"
                  }`}
                  data-testid={`vetrina-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="luxury-card p-2 max-w-2xl">
        {blocks.map((b, i) => (
          <div key={b.block_key} draggable={canWrite} onDragStart={()=>canWrite && onDragStart(i)} onDragOver={onDragOver} onDrop={()=>canWrite && onDrop(i)}
                className="flex items-center justify-between p-4 border-b border-[#E5E5E0] last:border-b-0 hover:bg-[#FAF9F6]"
                data-testid={`vetrina-block-${activeTab}-${b.block_key}`}>
            <div className="flex items-center gap-4">
              {canWrite && <GripVertical className="w-4 h-4 text-[#C5B39B]" strokeWidth={1.5} />}
              <span className="text-[10px] tracking-luxury text-[#707070] uppercase w-8">{i+1}</span>
              <span className="text-sm font-medium">{b.label}</span>
            </div>
            {canWrite ? <button onClick={()=>toggle(i)}
                    className={`flex items-center gap-2 text-xs tracking-luxury uppercase ${b.is_enabled ? "text-[#1A1A1A]" : "text-[#707070]"}`}
                    data-testid={`vetrina-block-toggle-${activeTab}-${b.block_key}`}>
              {b.is_enabled ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}
              {b.is_enabled ? "Visibile" : "Nascosto"}
            </button> : <span className="text-xs text-[#707070]" data-testid={`vetrina-block-status-${activeTab}-${b.block_key}`}>{b.is_enabled ? "Visibile" : "Nascosto"}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------- Product management tab ------------------------ */
function GestioneProdottiTab() {
  const { canWrite, subscriptionDisabled } = useAuth();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    api.get("/brands").then(r => {
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setBrands(items);
    }).catch(() => {});
  }, []);

  const loadProducts = (bid) => {
    if (!bid) { setProducts([]); return; }
    setLoading(true);
    api.get("/products/catalog-list", { params: { brand_id: bid } })
      .then(r => setProducts(r.data.items || []))
      .catch(() => toast.error("Errore nel caricamento prodotti"))
      .finally(() => setLoading(false));
  };

  const onBrandChange = (e) => {
    const bid = e.target.value;
    setBrandId(bid);
    setSearch("");
    loadProducts(bid);
  };

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return products;
    return products.filter(p =>
      (p.name || "").toLowerCase().includes(t) ||
      (p.code || "").toLowerCase().includes(t));
  }, [products, search]);

  const visibleCount = products.filter(p => p.qr_enabled).length;

  const toggleVisibility = async (p) => {
    const next = !p.qr_enabled;
    setSavingId(p.id);
    // optimistic update
    setProducts(prev => prev.map(x => x.id === p.id ? { ...x, qr_enabled: next } : x));
    try {
      await api.post(`/products/${p.id}/qr-visibility`, { qr_enabled: next });
    } catch {
      // revert on failure
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, qr_enabled: !next } : x));
      toast.error("Salvataggio non riuscito, riprova");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div data-testid="catalog-products-tab">
      <div className="flex flex-col md:flex-row md:items-end gap-4 mb-6 max-w-3xl">
        <div className="md:w-72">
          <label className="label-tiny">Brand</label>
          <select className="input-luxury" value={brandId} onChange={onBrandChange}
                  data-testid="catalog-brand-select">
            <option value="">— Seleziona un brand —</option>
            {brands.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="label-tiny">Cerca prodotto (nome o codice)</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" strokeWidth={1.5}/>
            <input type="text" value={search} onChange={(e)=>setSearch(e.target.value)}
                   placeholder="Es. LOVE002 oppure Gel Clear…"
                   disabled={!brandId}
                   className="input-luxury !pl-11 disabled:opacity-50"
                   data-testid="catalog-search-input" />
          </div>
        </div>
      </div>

      {!brandId ? (
        <div className="luxury-card p-10 text-center text-sm text-[#707070]" data-testid="catalog-empty-hint">
          <Store className="w-6 h-6 mx-auto mb-3 text-[#C5B39B]" strokeWidth={1.25}/>
          Seleziona un brand per visualizzarne i prodotti.
        </div>
      ) : loading ? (
        <div className="luxury-card p-10 text-center text-sm text-[#707070]">Caricamento prodotti…</div>
      ) : products.length === 0 ? (
        <div className="luxury-card p-10 text-center text-sm text-[#707070]">
          Nessun prodotto associato a questo brand.
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 max-w-3xl">
            <div className="text-xs text-[#707070]">
              {filtered.length} di {products.length} prodotti · <span className="text-[#4A6B53]">{visibleCount} configurati visibili</span> nel catalogo {subscriptionDisabled && "(catalogo disattivato)"}
            </div>
          </div>
          <div className="luxury-card divide-y divide-[#E5E5E0] max-w-3xl" data-testid="catalog-product-list">
            {filtered.map(p => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3"
                   data-testid={`catalog-product-row-${p.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Package className="w-4 h-4 text-[#C5B39B] shrink-0" strokeWidth={1.5}/>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-[11px] font-mono text-[#707070]">{p.code}</div>
                  </div>
                  {!p.is_active && (
                    <span className="text-[10px] tracking-luxury uppercase text-[#8C3A3A] ml-1">Non attivo</span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-[10px] tracking-luxury uppercase w-16 text-right font-medium ${p.qr_enabled ? "text-[#4A6B53]" : "text-[#A08D6B]"}`}>
                    {p.qr_enabled ? "Visibile" : "Nascosto"}
                  </span>
                  {canWrite && <Switch checked={!!p.qr_enabled}
                          disabled={savingId === p.id}
                          onCheckedChange={()=>toggleVisibility(p)}
                          className="h-6 w-11 border border-[#C9C0AE] shadow-inner data-[state=checked]:!bg-[#4A6B53] data-[state=unchecked]:!bg-[#CFC7B6] [&>span]:h-5 [&>span]:w-5 [&>span]:bg-white [&>span]:shadow-md data-[state=checked]:[&>span]:translate-x-5 data-[state=unchecked]:[&>span]:translate-x-0"
                          data-testid={`catalog-toggle-${p.id}`} />}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#707070]">
                Nessun prodotto trovato per “{search}”.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* --------------------------------- Page ------------------------------------ */
const MAIN_TABS = [
  { key: "settings", label: "Impostazioni", icon: SlidersHorizontal },
  { key: "products", label: "Gestione Prodotti", icon: Package },
];

export default function VetrinaLayoutPage() {
  const [mainTab, setMainTab] = useState("settings");

  return (
    <div className="p-4 pt-16 md:p-8 lg:p-12" data-testid="vetrina-layout-page">
      <div className="mb-6">
        <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Configurazione</div>
        <h1 className="font-serif text-3xl md:text-4xl">Catalogo</h1>
      </div>

      {/* Main tabs */}
      <div className="flex gap-8 border-b border-[#E5E5E0] mb-8" data-testid="catalog-main-tabs">
        {MAIN_TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key}
                    onClick={() => setMainTab(t.key)}
                    className={`pb-3 flex items-center gap-2 text-sm transition-colors ${
                      mainTab === t.key
                        ? "text-[#1A1A1A] border-b-2 border-[#1A1A1A] -mb-px font-medium"
                        : "text-[#707070] hover:text-[#1A1A1A]"
                    }`}
                    data-testid={`catalog-main-tab-${t.key}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5}/>{t.label}
            </button>
          );
        })}
      </div>

      {mainTab === "settings" ? <ImpostazioniTab /> : <GestioneProdottiTab />}
    </div>
  );
}
